import type { CustomDataMap } from './subscriberCustomFields';
import type { SubscriberRow } from '../components/Subscribers/types';

/** Candidate keys for profile fields (mirrors import-connections-to-subscribers.mjs). */
const PROFILE_FIELD_KEYS = {
  icp: ['icp'],
  callMeWhen1: ['call_me_when_1', 'call_me_when1', 'callmewhen1', 'call_me_when_one'],
  callMeWhen2: ['call_me_when_2', 'call_me_when2', 'callmewhen2', 'call_me_when_two'],
  callMeWhen3: ['call_me_when_3', 'call_me_when3', 'callmewhen3', 'call_me_when_three'],
  linkedin_url: ['linkedin_url', 'linkedin', 'linked_url', 'linkedinurl'],
} as const;

export type SubscriberProfileFields = {
  name: string | null;
  email: string | null;
  role: string | null;
  icp: string | null;
  callMeWhen1: string | null;
  callMeWhen2: string | null;
  callMeWhen3: string | null;
  linkedin_url: string | null;
};

function readString(val: unknown): string | null {
  if (typeof val !== 'string') return null;
  const t = val.trim();
  return t || null;
}

function readFromCustomData(customData: CustomDataMap | null | undefined, candidates: readonly string[]): string | null {
  if (!customData || typeof customData !== 'object') return null;
  for (const key of candidates) {
    const v = readString(customData[key]);
    if (v) return v;
  }
  return null;
}

/** Read ICP / call-me-when / LinkedIn from subscriber custom_data + role. */
export function readSubscriberProfileFields(row: SubscriberRow | null | undefined): SubscriberProfileFields {
  if (!row) {
    return {
      name: null,
      email: null,
      role: null,
      icp: null,
      callMeWhen1: null,
      callMeWhen2: null,
      callMeWhen3: null,
      linkedin_url: null,
    };
  }

  const customData = (row.custom_data ?? {}) as CustomDataMap;

  return {
    name: readString(row.name),
    email: readString(row.email),
    role: readString(row.role),
    icp: readFromCustomData(customData, PROFILE_FIELD_KEYS.icp),
    callMeWhen1: readFromCustomData(customData, PROFILE_FIELD_KEYS.callMeWhen1),
    callMeWhen2: readFromCustomData(customData, PROFILE_FIELD_KEYS.callMeWhen2),
    callMeWhen3: readFromCustomData(customData, PROFILE_FIELD_KEYS.callMeWhen3),
    linkedin_url: readFromCustomData(customData, PROFILE_FIELD_KEYS.linkedin_url),
  };
}

export function profileFieldsToEmailContext(profile: SubscriberProfileFields): string {
  const lines: string[] = [];
  if (profile.name) lines.push(`- Name: ${profile.name}`);
  if (profile.email) lines.push(`- Email: ${profile.email}`);
  if (profile.role) lines.push(`- Role: ${profile.role}`);
  if (profile.linkedin_url) lines.push(`- LinkedIn: ${profile.linkedin_url}`);
  lines.push('');
  lines.push('ICP (Ideal Customer Profile):');
  lines.push(profile.icp?.trim() || 'No ICP on file');
  lines.push('');
  lines.push('Call Me When Statements:');
  lines.push(`1. ${profile.callMeWhen1?.trim() || 'Not set'}`);
  lines.push(`2. ${profile.callMeWhen2?.trim() || 'Not set'}`);
  lines.push(`3. ${profile.callMeWhen3?.trim() || 'Not set'}`);
  return lines.join('\n');
}
